/**
 * DashboardPage — page d'accueil de l'app ERP après connexion.
 *
 * Composition de briques branchées sur des données RÉELLES : en-tête + carte
 * domaine (parcellaire), hero échéances cave (`planCave.statsResume`), pouls
 * commercial (`venteDocuments.statsResume`, Lot G), météo, stock bouteilles
 * (Cave), alertes matériel (`parcMateriel.listerAlertes`). Les widgets 100%
 * fictifs des modules non livrés ont été retirés — aucune fausse donnée sur le
 * dashboard (le pouls commercial affiche un état vide honnête si aucune vente).
 *
 * Choix de palette validé avec Max :
 * - CTA primaires + accents identitaires : gold (`champagne-600`)
 * - Aubergine cantonné aux tags Brut Tradition + dots info + avatar utilisateur
 * - Vert (vine) : status OK, indicateurs hausse, tag Réserve
 * - Rust : warnings sérieux (jamais rouge vif)
 *
 * Architecture :
 * - Briques génériques transverses → `@champ-erp/ui` (Card, Tag, Sparkline…)
 * - Layout ERP (sidebar/topbar) → `apps/erp/src/components/layout/`
 * - Briques 100% dashboard (hero, widgets, jauge, alertes) → sous-dossier
 *   `./dashboard/` pour garder cette page en pure composition (≤300 lignes).
 */

import { Eyebrow, Fab } from "@champ-erp/ui";
import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";

import { initiales as toInitiales, nomComplet as toNomComplet, roleLabel } from "../auth/format.js";
import { useMe } from "../auth/use-me.js";
import { formatDateLongFr } from "../lib/format-dates.js";
import { formatSurfaceHectares } from "../lib/format-surfaces.js";
import { trpc } from "../lib/trpc.js";
import { LIBELLE_VERSION_BUILD } from "../lib/version-build.js";

import { EcheancesDeclaratives } from "./conformite/index.js";
import { BlocAlertes } from "./dashboard/BlocAlertes.js";
import { HeroEcheance } from "./dashboard/HeroEcheance.js";
import { OnboardingBanner } from "./dashboard/OnboardingBanner.js";
import { useActionsRapides } from "./dashboard/useActionsRapides.js";
import { VignobleBanner } from "./dashboard/VignobleBanner.js";
import { WidgetActiviteRecente } from "./dashboard/WidgetActiviteRecente.js";
import { WidgetMeteoTimeline } from "./dashboard/WidgetMeteoTimeline.js";
import { WidgetPerformanceCuvee } from "./dashboard/WidgetPerformanceCuvee.js";
import { WidgetPoulsCommercial } from "./dashboard/WidgetPoulsCommercial.js";
import { WidgetRendementParcelle } from "./dashboard/WidgetRendementParcelle.js";
import { WidgetStockParCepage } from "./dashboard/WidgetStockParCepage.js";
import { WidgetStockVivant } from "./dashboard/WidgetStockVivant.js";
import { WidgetVentesMois } from "./dashboard/WidgetVentesMois.js";

// ---------- Données réelles : utilisateur + domaine actif via auth.me ----------
//
// `AppShellLayout` (parent route) garantit que cette page n'est rendue que si
// `auth.me` a déjà répondu 200 et qu'on a un utilisateur (via <RequireAuth>).
// On peut donc lire sans test de chargement ici. Les champs encore absents en
// BDD (surface, cépages, localisation) sont gérés cas par cas dans le rendu.

interface DonneesUtilisateur {
  prenom: string;
  initiales: string;
  nomComplet: string;
  role: string;
}

interface DonneesDomaine {
  /** Nom commercial / raison sociale (ex : "Champagne Lefèvre & Fils"). */
  nom: string;
  /** Code court réglementaire (RM / NM / RC…) — affiché dans l'en-tête. */
  statutCode: string;
}

// ---------- Page ----------

export function DashboardPage() {
  // Garanti non-null par <RequireAuth> qui ne rend ses enfants qu'après
  // résolution de auth.me. Le `!` sur data est sûr ici.
  const { data } = useMe();
  const me = data!;

  // Membership active = celle dont le domaine_id matche `domaine_id_actif`.
  // Si le user n'a pas encore choisi de tenant (cas multi-domaines), on prend
  // la première membership accessible — la sidebar offrira plus tard un
  // sélecteur explicite.
  const activeMembership =
    me.domaines_accessibles.find((m) => m.domaine.id === me.domaine_id_actif) ??
    me.domaines_accessibles[0] ??
    null;

  const utilisateur: DonneesUtilisateur = {
    prenom: me.utilisateur.prenom,
    initiales: toInitiales(me.utilisateur.prenom, me.utilisateur.nom),
    nomComplet: toNomComplet(me.utilisateur.prenom, me.utilisateur.nom),
    role: roleLabel(activeMembership?.role_code ?? me.role_code_actif),
  };

  // Le shell (AppShellLayout) gère sidebar + topbar — ici on rend uniquement
  // le contenu spécifique au tableau de bord à l'intérieur de la zone scrollable.
  const domaine: DonneesDomaine = activeMembership
    ? {
        nom: activeMembership.domaine.raison_sociale,
        statutCode: activeMembership.domaine.statut_professionnel,
      }
    : {
        nom: "Domaine à configurer",
        statutCode: "—",
      };

  // Actions du Fab « + » : filtrées par permission + contexte (cf hook).
  const actionsRapides = useActionsRapides();

  return (
    <>
      {/* ORDRE = du plus ACTIONNABLE/urgent (haut) au plus contextuel (bas).
          Un vigneron regarde son tableau de bord ~5 s le matin : la 1ʳᵉ question
          est « ai-je un retard, une rupture, une échéance à traiter ? ». Chaque
          widget se masque seul s'il manque la permission (ni titre ni cadre
          vide) — l'ordre n'affecte donc pas le gating. */}
      <main className="mx-auto w-full max-w-[1440px] space-y-6 p-4 sm:p-8">
        <EnTetePage utilisateur={utilisateur} domaine={domaine} />
        <OnboardingBanner />
        <VignobleBanner />

        {/* La météo conditionne directement les travaux du jour : elle reste
            visible dès l'ouverture, avant les échéances et indicateurs. */}
        <WidgetMeteoTimeline />

        {/* ── Zone 1 · À TRAITER / urgent ─────────────────────────────────
            Ce qui exige une action ou est en retard remonte tout en haut. */}
        {/* 1a. Échéances déclaratives (DRM/DR/DS) : un retard = pénalités
            Douanes, le risque le plus coûteux → en tête, compte à rebours urgent
            (DatePill rust) bien visible. */}
        <EcheancesDeclaratives />
        {/* 1b. Alertes : stock commercialisable sous seuil (rupture de vente) +
            contrôles matériel dus (dont réglementaires « Obligatoire »). */}
        <BlocAlertes />
        {/* 1c. Prochaine mise en marché / dégorgement — hero cave (carte
            sombre). Actionnable mais à échéance future → ferme la zone urgente. */}
        <HeroEcheance />

        {/* ── Zone 2 · POULS COMMERCIAL (l'argent) ────────────────────────
            CA du mois, à encaisser (retards en rust), à livrer, devis + tendance. */}
        <Grille2Colonnes>
          <WidgetPoulsCommercial />
          <WidgetVentesMois />
        </Grille2Colonnes>

        {/* ── Zone 3 · PRODUCTION / contexte ──────────────────────────────
            Où en est la production : vignoble (rendement, encépagement) puis
            cave (performance cuvée, stock bouteilles). */}
        <Grille2Colonnes>
          <WidgetRendementParcelle />
          <WidgetStockParCepage />
        </Grille2Colonnes>
        <WidgetPerformanceCuvee />
        <WidgetStockVivant />

        {/* ── Zone 4 · ACTIVITÉ RÉCENTE ──────────────────────────────────── */}
        <WidgetActiviteRecente />
      </main>
      <DashboardFooter />
      {/* Fab « + » : entrée mobile prioritaire, ancré bas-droite. Monté ici
          (dashboard) plutôt que dans le shell → moindre risque vs verticales //. */}
      <Fab actions={actionsRapides} />
    </>
  );
}

// Grille responsive 2 colonnes (desktop) → empilée (mobile) au breakpoint 1100px,
// commun à tous les binômes de widgets du dashboard.
function Grille2Colonnes({ children }: { children: ReactNode }) {
  return <section className="grid grid-cols-1 gap-6 min-[1100px]:grid-cols-2">{children}</section>;
}

// Footer ERP minimaliste — réassurance + version + liens support.
// Bien plus discret que celui de la landing : on n'est pas en navigation
// publique, le vigneron a déjà tout le contexte applicatif.
function DashboardFooter() {
  return (
    <footer className="mx-auto w-full max-w-[1440px] px-4 pb-6 pt-2 sm:px-8">
      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-champagne-900/10 pt-4 text-[12px] text-champagne-700">
        <div className="flex flex-wrap items-center gap-2">
          <span>Conçu pour la filière Champagne</span>
          <span className="mono text-champagne-700/60">·</span>
          <span className="mono">Crayéa · {LIBELLE_VERSION_BUILD}</span>
        </div>
        {/* « Mentions légales » et « CGU » étaient des `href="#"` neutralisés
            par preventDefault : un clic ne faisait rien. Retirés jusqu'à ce
            qu'il existe une cible (page ERP ou lien vitrine configurable). */}
        <nav aria-label="Liens secondaires" className="flex items-center gap-4">
          <Link to="/aide" className="hover:text-champagne-900">
            Centre d&apos;aide
          </Link>
        </nav>
      </div>
    </footer>
  );
}

// ---------- En-tête de page ----------

/**
 * En-tête : salutation prénom à gauche + carte d'identité du domaine à droite.
 *
 * La carte domaine affiche le statut + surface vignoble (depuis le module
 * Parcellaire Lot B.5). Si aucune parcelle, un lien CTA invite à configurer
 * le vignoble.
 */
function EnTetePage({
  utilisateur,
  domaine,
}: {
  utilisateur: DonneesUtilisateur;
  domaine: DonneesDomaine;
}) {
  const aujourdhui = formatDateLongFr(new Date());
  // Stats vignoble : 1 requête légère pour le compteur de parcelles + surface.
  // staleTime hérite du défaut global (5 min) — suffisant : un user qui ajoute
  // une parcelle invalidate ce cache via `parcellaire.statsResume` queryKey
  // (cf NouvelleParcelle.tsx).
  const { data: stats } = useQuery({
    queryKey: ["parcellaire.statsResume"],
    queryFn: () => trpc.parcellaire.statsResume.query(),
  });

  return (
    <section className="flex flex-wrap items-end justify-between gap-6">
      <div>
        <Eyebrow>Pilotage · Vue générale</Eyebrow>
        {/* `letterSpacing` inline conservé (valeur précise de la maquette,
            -0.015em). On ne cumule plus avec `tracking-tight` qui valait
            -0.025em : Tailwind aurait été écrasé inline → bruit visuel
            dans le code source. */}
        {/* H1 en Fraunces (serif éditorial) — convention produit : tous les
            écrans hub & paramétrage utilisent Fraunces pour le h1.
            Les futurs écrans d'outil terrain (saisie pesée, manipulations cave)
            pourront rester en Inter sans-serif si la lisibilité l'impose. */}
        <h1
          className="mt-1.5 font-serif text-[34px] font-semibold tracking-tight"
          style={{ letterSpacing: "-0.015em" }}
        >
          Tableau de bord
        </h1>
        <p className="mt-1.5 text-[14px] text-champagne-800">
          {aujourdhui} · <span className="text-champagne-900">Bonjour, {utilisateur.prenom}</span>
        </p>
      </div>
      {/* Aligné à GAUCHE sous `sm` : en mobile ce bloc passe à la ligne sous le
          titre, et un alignement à droite le détachait visuellement de tout le
          reste de la page. À partir de `sm` il reprend sa place en vis-à-vis
          du titre, donc à droite. */}
      <div className="leading-tight sm:text-right">
        <Eyebrow>Domaine</Eyebrow>
        {/* `statutCode` (RM/NM/RC…) suffit ici : le vigneron connaît son
            propre statut, pas la peine de dérouler le libellé long juste
            en dessous. Le libellé complet reste disponible côté Paramètres
            et dans les écrans réglementaires où il est utile. */}
        <div className="mt-1 text-[14px] text-champagne-800">
          {domaine.nom} · {domaine.statutCode}
        </div>
        {/* Indicateur surface : affiché uniquement si au moins 1 parcelle.
            Si 0 parcelle, le bandeau <VignobleBanner> (en haut du dashboard)
            prend le relais — pas besoin de doublonner le rappel ici.
            Lien vers /vignoble : raccourci pédagogique (le vigneron lit son
            chiffre, clique, atterrit sur la liste). Soulignement au hover
            pour signaler l'affordance sans alourdir l'aplat. */}
        {stats && stats.nb_parcelles > 0 && (
          <Link
            to="/vignoble"
            className="mt-0.5 inline-block text-[12px] text-champagne-700 underline-offset-2 transition-colors hover:text-aubergine-900 hover:underline"
            title="Voir mes parcelles"
          >
            {stats.nb_parcelles} parcelle{stats.nb_parcelles > 1 ? "s" : ""}
            {" · "}
            {formatSurfaceHectares(stats.surface_totale_ares)}
          </Link>
        )}
      </div>
    </section>
  );
}
